// The LIB operations console route subtree (console slice — serials + transit +
// stocktake + reports). Code-based routes per DEV-GUIDE-002 §4 — a package-composed
// subtree hand-writes `.lazy()` + `createLazyRoute`. This factory hangs the
// `/library/operations` tree off the portal's root route so router.tsx stays the
// single wiring point (composed ALONGSIDE the other lib subtrees — none touches
// another).
//
// GATING is per-screen (router.tsx's documented choice). This subtree bundles four
// operational surfaces with DIFFERENT API tiers, so the LAYOUT route gates on the
// broadest read that any tab needs — lib:catalog.read (serials read is the entry) —
// and each tab re-gates to match its API guard, a defence-in-depth mirror
// (ADR-001 §14.2, the UI can only HIDE; the gateway is the authority):
//   · Serials  — reads lib:catalog.read, writes lib:catalog  → tab guards writes
//   · Transit  — lib:desk (the desk ship/receive log)        → SCREEN <Can>-gates lib:desk
//   · Stocktake — lib:admin (fee-waiving reconcile)           → SCREEN <Can>-gates lib:admin
//   · Reports  — lib:reports (revenue/storage analytics)      → SCREEN <Can>-gates lib:reports
// Anonymous → /login; signed-in-without-lib:catalog.read → an explicit denied
// panel, never a blank Outlet.
//
// URL-STATE + LOADERS. The three keyset-list routes carry a self-healing
// `validateSearch` (DEV-GUIDE-002 §4.1) — a hand-written validator with the same
// `.catch()` contract as the Zod list query (zod isn't a direct portal dep), so a
// garbage `?status=`/`?direction=` heals to the default rather than throwing.
// Only SERIALS gets a `loader` → `void prefetchInfiniteQuery` (its query tier,
// lib:catalog.read, MATCHES the layout gate, so the warm-up never fires a request
// the viewer can't make); it warms the FIRST page only, so the reader's scroll
// fetches page two, never the loader. TRANSIT (lib:desk) + STOCKTAKE (lib:admin)
// sit a tier ABOVE the layout, so their lists are fetched by the screen's
// `useInfiniteQuery` INSIDE its `<Can>` gate — an eager loader there would fire a
// 403 for a lib:catalog-only viewer who can still see the tab. The detail
// sub-lists (Kardex issues, scans, alerts) stream from a LOCAL `useInfiniteQuery`
// on their screens, so those routes stay plain lazy stubs.
//
// NO `cursor` IN THE URL ANY MORE (2026-09-22, the owner: "now let's do it with
// every table and card listing of every portal"). Every one of these registers
// is an INFINITE stream: the facets still live in the URL — a shared link
// restores exactly the log someone was reading — but the cursor does not,
// because it no longer names a page. It names a position inside one
// accumulating list, which is a different amount of scrolling for every reader.
// A stale `?cursor=` on an old bookmark simply falls out of the validator, which
// is the self-healing contract these validators already had.
//
// PANEL URL CONTRACT (the 2026-08-22 product decision). Every input FORM and
// every VIEWER on this subtree is an @erp/ui/side-panel SidePanel driven by
// `?panel=<name>&panelId=<id>&panelWide=1`, so Back closes it, a pasted link
// opens straight into the record, and the list underneath keeps its cursor and
// filters. `panelSearch(names, input)` heals those three params against a CLOSED
// set of names PER ROUTE — spread below alongside each route's existing
// validator. A route that owns no panel gets no panel params. Confirmations stay
// modals and therefore stay OUT of the URL: an "are you sure?" is not a view.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { libraryQueries } from "../../index.js";
import { TRANSIT_DIRECTIONS, TRANSIT_STATUSES, AUDIT_SESSION_STATUSES } from "../../../schemas/index.js";
import type { AuditSessionStatus, TransitDirection, TransitStatus } from "../../../schemas/index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { HubGrid } from "@erp/ui/hub-grid";
import { PageHeading } from "@erp/ui/page-heading";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { ClipboardCheck } from "lucide-solid";
import { panelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ─────────────────────────── keyset page sizes ─────────────────────────── */
export const SERIALS_PAGE_SIZE = 20;
export const TRANSIT_PAGE_SIZE = 20;
export const STOCKTAKE_PAGE_SIZE = 20;

/* ── The closed panel-name set per route (see the PANEL URL CONTRACT note). ── */
export const SERIALS_PANELS = ["renew"] as const;
// "history" is the record's AUDIT TRAIL (@erp/module-audit RecordHistory) — a
// separate surface from the Kardex issue register, which is the subscription's
// own data and stays on the page.
export const SERIAL_DETAIL_PANELS = ["receive", "renew", "edit", "history"] as const;
export const TRANSIT_PANELS = ["new"] as const;
export const STOCKTAKE_PANELS = ["new"] as const;
export const LOST_DAMAGED_PANELS = ["flag"] as const;
export const TRANSACTIONS_PANELS = ["detail"] as const;
export const AUDIT_PANELS = ["detail"] as const;

/* ── SERIALS has no list search state left. Source parity means the register
 *    carries no facet filters (SerialListQuery is a cursor plus a text search
 *    the screen owns locally), and the cursor left the URL with the paging — so
 *    the route validates its panel params and nothing else, and the
 *    `validateSerialsSearch` helper that used to heal the cursor is gone with
 *    it. An old `?cursor=` bookmark still heals: a validator's return value IS
 *    the search, so a param nobody validates simply falls out. ── */

/* ── Transit URL-as-state: ?status / ?direction closed-enum filters (no cursor
 *    — the log streams; see the header). ── */
export interface TransitSearch {
  status?: TransitStatus;
  direction?: TransitDirection;
}
export function validateTransitSearch(input: Record<string, unknown>): TransitSearch {
  const status = TRANSIT_STATUSES.includes(input.status as TransitStatus) ? (input.status as TransitStatus) : undefined;
  const direction = TRANSIT_DIRECTIONS.includes(input.direction as TransitDirection)
    ? (input.direction as TransitDirection)
    : undefined;
  return { status, direction };
}

/* ── Stocktake URL-as-state: ?status closed-enum filter (no cursor — the
 *    register streams; see the header). ── */
export interface StocktakeSearch {
  status?: AuditSessionStatus;
}
export function validateStocktakeSearch(input: Record<string, unknown>): StocktakeSearch {
  const status = AUDIT_SESSION_STATUSES.includes(input.status as AuditSessionStatus)
    ? (input.status as AuditSessionStatus)
    : undefined;
  return { status };
}

const OPERATIONS_LINKS = [
  { to: "/library/operations/serials", label: "Serials", blurb: "Subscriptions and the Kardex", id: "operations-nav-serials" },
  { to: "/library/operations/transit", label: "Transit", blurb: "Items sent to and received from other libraries", id: "operations-nav-transit" },
  { to: "/library/operations/stocktake", label: "Stocktake", blurb: "Count the shelves and reconcile what is missing", id: "operations-nav-stocktake" },
  { to: "/library/operations/reports", label: "Reports", blurb: "Revenue, storage and catalogue figures", id: "operations-nav-reports" },
] as const;

/**
 * The operations shell: gates on lib:catalog.read, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (module manifest) + header breadcrumbs.
 */
function OperationsLayout() {
  return (
    <ConsoleLayout
      perm="lib:catalog.read"
      deniedMessage="This account cannot open operations. Ask an administrator for catalogue access."
      skeletonId="operations-skeleton"
      deniedId="operations-denied"
      unreachableId="operations-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/operations` index — the landing with cards into each operational surface. */
function OperationsLanding() {
  return (
    <div>
      {/* The console's identity capsule (DEV-GUIDE-002 §12.5): every operations
          screen wears chartreuse, so the landing they hang off wears it too. The
          glyph is the sidebar section's own (ClipboardCheck). */}
      <PageHeading
        icon={ClipboardCheck}
        hue={MODULE_HUE.operations}
        title="Operations"
        subtitle="Serials, inter-library transit, stocktake and reports"
        help={{
          title: "The library's day-to-day",
          children: (
            <p>
              The work that keeps the collection in order. Serials tracks the journals and magazines that arrive
              issue by issue; Transit follows items sent to and received from other libraries; Stocktake counts
              the shelves and reconciles what is missing; Reports gives the revenue, storage and catalogue
              figures. Each card opens one of those surfaces.
            </p>
          ),
        }}
      />
      {/* Was a hand-drawn grid of Link > Card > CardHeader with a green
          `StatusBadge label="Open"` for a button — see @erp/ui/hub-grid for
          why both were wrong, and for the one place that shape now lives. */}
      <HubGrid id="operations-landing" items={OPERATIONS_LINKS} hue={MODULE_HUE.operations} columns={3} />
    </div>
  );
}

/**
 * Build the `/library/operations` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Static
 * `serials/new` and `stocktake` are registered before their dynamic siblings
 * (`serials/$serialId`, `stocktake/$sessionId`); path ranking makes static win
 * regardless, but the order documents intent.
 */
export function makeLibOperationsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const operationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/operations",
    component: OperationsLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "/",
    component: OperationsLanding,
  });

  // Serials list — an accumulating STREAM. The loader warms the SAME cache entry
  // the screen's `useInfiniteQuery` re-keys (its tier === the layout gate, so the
  // warm-up is always allowed), and `prefetchInfiniteQuery` warms the FIRST page
  // only (its `pages` default is 1). The filters object here MUST equal the
  // screen's to share a cache key — the screen adds its search only once one is
  // typed, so the unsearched entry is exactly this one.
  const serialsRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "serials",
    validateSearch: (input: Record<string, unknown>) => panelSearch(SERIALS_PANELS, input),
    loader: ({ context }) => {
      void context.queryClient.prefetchInfiniteQuery(libraryQueries.serialsInfinite({ limit: SERIALS_PAGE_SIZE }));
    },
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/Serials.js")));

  const serialNewRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "serials/new",
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/SerialForm.js")));

  const serialDetailRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "serials/$serialId",
    validateSearch: (input: Record<string, unknown>) => panelSearch(SERIAL_DETAIL_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/SerialDetail.js")));

  // Transit list — self-healing ?status/?direction, but NO loader: the
  // list rides lib:desk (a tier above the layout), so the screen fetches inside
  // its own <Can> gate to avoid a premature 403 for a lib:catalog-only viewer.
  const transitRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "transit",
    validateSearch: (input: Record<string, unknown>) => ({
      ...validateTransitSearch(input),
      ...panelSearch(TRANSIT_PANELS, input),
    }),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/InterLibraryTransit.js")));

  // Stocktake list — self-healing ?status, NO loader (rides lib:admin —
  // the transit reasoning; the screen fetches inside its <Can> gate).
  const stocktakeRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "stocktake",
    validateSearch: (input: Record<string, unknown>) => ({
      ...validateStocktakeSearch(input),
      ...panelSearch(STOCKTAKE_PANELS, input),
    }),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/Stocktake.js")));

  const stocktakeSessionRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "stocktake/$sessionId",
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/StocktakeSession.js")));

  const lostDamagedRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "lost-damaged",
    validateSearch: (input: Record<string, unknown>) => panelSearch(LOST_DAMAGED_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/LostDamaged.js")));

  const transactionsRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "transactions",
    validateSearch: (input: Record<string, unknown>) => panelSearch(TRANSACTIONS_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/TransactionHistory.js")));

  const auditRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "audit",
    validateSearch: (input: Record<string, unknown>) => panelSearch(AUDIT_PANELS, input),
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/AuditLog.js")));

  const reportsRoute = createRoute({
    getParentRoute: () => operationsRoute,
    path: "reports",
  }).lazy(lazyRoute(() => import("../../screens/admin/operations/Reports.js")));

  return [
    operationsRoute.addChildren([
      indexRoute,
      serialsRoute,
      serialNewRoute,
      serialDetailRoute,
      transitRoute,
      stocktakeRoute,
      stocktakeSessionRoute,
      lostDamagedRoute, transactionsRoute, reportsRoute, auditRoute,
    ]),
  ];
}
